import {
  DigitalChannelType,
  InteractionTabType,
  MediaType,
} from '@nice-ccf/core-sdk';
import {
  selectInboxCollapsedState,
  setIsDocked,
  updateInboxCollapsed,
  updateAppSpaceTab,
  fetchAgentContactGroup,
  setNewCommitment,
  updateActiveContact,
} from '@nice-ccf/ui-components';
import {
  CcfAddChannelIcon,
  CcfChatIcon,
  CcfDockControlIcon,
  CcfFacebookIcon,
  CcfHeader,
  CcfInstaIcon,
  CcfLinkedinIcon,
  CcfMailOutlineIcon,
  CcfMessageOutlineIcon,
  CcfPhoneInboundIcon,
  CcfPinControlIcon,
  CcfPopOver,
  CcfSmsIcon,
  CcfTypography,
  CcfWhatsAppIcon,
  useTranslator,
  CcfUpArrowIcon,
  CcfEmailIcon,
  CcfVoiceMailIcon,
  CcfCallCommitIcon,
  CcfXIcon,
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import { Contact } from '../../model/contact';
import styles from './contact-control-panel-heading.module.scss';
import { useState } from 'react';
import { PopOverMenuItem } from '../../model/callControlPopover';
import { Divider } from '@mui/material';
import { getInteractionData } from '../../../ccf-interaction-workspace/ccf-interaction-mock-data';
import { AppSpace } from '../../../../enums/app-space-tabs';

/* eslint-disable-next-line */
export interface ContactControlPanelHeadingProps {
  contact: Contact;
  headerText?: string;
  headerTextClassess?: string;
}

const IconsLeftMap = new Map([
  [
    MediaType.VOICE.toString(),
    <CcfPhoneInboundIcon className="controlPanelHeaderLeftIcon phonefillColor" />,
  ],
  [
    MediaType.VOICEMAIL.toString(),
    <CcfVoiceMailIcon
      style={{ fill: '#007AB8' }}
      className="controlPanelHeaderLeftIcon phonefillColor"
    />,
  ],
  [
    DigitalChannelType.INSTAGRAM.toString(),
    <CcfInstaIcon className="controlPanelHeaderLeftIcon" />,
  ],
  [
    DigitalChannelType.TWITTER.toString(),
    <CcfXIcon className="controlPanelHeaderLeftIcon" />,
  ],
  [
    DigitalChannelType.FACEBOOK.toString(),
    <CcfFacebookIcon className="controlPanelHeaderLeftIcon" />,
  ],
  [
    DigitalChannelType.LINKEDIN.toString(),
    <CcfLinkedinIcon className="controlPanelHeaderLeftIcon" />,
  ],
  [
    DigitalChannelType.CHAT.toString(),
    <CcfChatIcon className="controlPanelHeaderLeftIcon" viewBox="0 0 19 19" />,
  ],
  [
    DigitalChannelType.CHATWITHBOTRANSCRIPT.toString(),
    <CcfChatIcon className="controlPanelHeaderLeftIcon" viewBox="0 0 19 19" />,
  ],
  [
    DigitalChannelType.EMAIL.toString(),
    <CcfEmailIcon className="controlPanelHeaderLeftIcon" viewBox="0 0 19 9" />,
  ],
]);

/**
 * Component display control panel Heading, it cosnist of common header and dropdown items
 * @param props - ContactControlPanelHeadingProps
 * ```
 * @example -
 * const headerText = 'Liam Davis'
 * const headerTextClassess = 'controlButtonHeaderText'
 * <ContactControlPanelHeading headerText={headerText} headerTextClassess={'controlButtonHeaderText'} contact={contact} />
 * ```
 */
export function ContactControlPanelHeading(
  props: ContactControlPanelHeadingProps
) {
  const isInboxCollpased: boolean = useSelector(selectInboxCollapsedState);
  const dispatch = useDispatch();
  const [translate] = useTranslator();

  const initialPopOverMenuList = {
    popOverHeader: translate('controlPosition'),
    menuItems: [
      {
        items: [
          props.contact.isDocked
            ? {
                label: translate('pinControls'),
                icon: (
                  <CcfPinControlIcon className="actionIconStyle"></CcfPinControlIcon>
                ),
                type: 'pinControl',
                closeOnSelection: true,
              }
            : {
                label: translate('dockControls'),
                icon: (
                  <CcfDockControlIcon className="actionIconStyle"></CcfDockControlIcon>
                ),
                type: 'dockControls',
                closeOnSelection: true,
              },
          // {
          //   label: translate('addSms'),
          //   icon: <CcfMessageOutlineIcon className="actionIconStyle" />,
          //   type: 'sms',
          //   closeOnSelection: false,
          // },
          // {
          //   label: translate('addEmail'),
          //   icon: <CcfMailOutlineIcon className="actionIconStyle" />,
          //   type: 'email',
          //   closeOnSelection: false,
          // },
          {
            label: translate('addElevation'),
            icon: <CcfAddChannelIcon className="actionIconStyle" />,
            type: 'newChannel',
            closeOnSelection: true,
          },
          {
            label: translate('addCommitment'),
            icon: (
              <CcfCallCommitIcon
                style={{
                  fill: '#526b7a',
                  height: '16px',
                  width: '16px',
                  marginLeft: '6px',
                }}
                className="actionIconStyle"
              />
            ),
            type: 'addCommitment',
            closeOnSelection: true,
          },
        ],
      },
    ],
  };

  const [popOverMenuItems, setPopOverMenuItems] = useState(
    initialPopOverMenuList
  );
  const [showDetailedPopOverMenu, setShowDetailedPopOverMenu] = useState(false);
  /**
   * Function pins the contact control panel outside assignment box
   * @param event -React.MouseEvent<HTMLElement>
   * @example pinControl(event?)
   */
  const pinControl = (event?: React.MouseEvent<HTMLElement>) => {
    dispatch(setIsDocked({ id: props.contact.id, isDocked: false }));
    event?.stopPropagation();
  };
  /**
   * Functions docks the contact control panel to inbox card
   * @example dockControl()
   */
  const dockControl = () => {
    if (isInboxCollpased) {
      dispatch(updateInboxCollapsed(false));
    }
    dispatch(setIsDocked({ id: props.contact.id, isDocked: true }));
  };
  /**
   * Function to on selecting popover item selection
   * @param item -PopOverMenuItem
   * @param event -React.MouseEvent<HTMLElement>
   * @example - onPopOverItemSelection(item, e)
   */
  const onPopOverItemSelection =
    (item: PopOverMenuItem, event?: React.MouseEvent<HTMLElement>) => () => {
      switch (item.type) {
        case 'pinControl':
          return pinControl(event);
        case 'dockControls':
          return dockControl();
        // case 'sms':
        //   setPopOverMenuItems({
        //     popOverHeader: translate('sms'),
        //     menuItems: [
        //       {
        //         items: [
        //           {
        //             label: translate('back'),
        //             icon: (
        //               <CcfUpArrowIcon className="actionIconStyle backArrowIcon" />
        //             ),
        //             type: 'backToInitialMenu',
        //             closeOnSelection: false,
        //           },
        //           {
        //             label: props.contact.messageType,
        //             icon: <CcfSmsIcon className="actionIconStyle" />,
        //             type: 'smsChat',
        //             closeOnSelection: true,
        //           },
        //           {
        //             label: props.contact.messageType,
        //             icon: <CcfWhatsAppIcon className="actionIconStyle" />,
        //             type: 'whatsapp',
        //             closeOnSelection: true,
        //           },
        //         ],
        //       },
        //     ],
        //   });
        //   setShowDetailedPopOverMenu(true);
        //   return;
        // case 'smsChat':
        //   if (showDetailedPopOverMenu) {
        //     dispatch(
        //       updateContactInteraction({
        //         id: props.contact.id,
        //         interaction: getInteractionData(InteractionTabType.SMS),
        //       })
        //     );
        //     // dispatch(setInteractionHeaderTabs({ id: props.contact.id, contactType: InteractionTabType.SMS }));
        //   }
        //   return;
        case 'newChannel':
          dispatch(updateAppSpaceTab({ tab: AppSpace.DIRECTORY }));
          dispatch(fetchAgentContactGroup('people'));
          dispatch(
            updateActiveContact({
              id: props.contact.id|| '',
              updates: { isElevated: true },
            })
          );
          return;
        //   return;
        case 'addCommitment':
          //event.stopPropagation();
          dispatch(setNewCommitment(true));
          dispatch(updateAppSpaceTab({ tab: AppSpace.SCHEDULE }));
          return;
        case 'backToInitialMenu':
          navigateToInitialPopOverState();
          return;
        default:
          return;
      }
    };
  /**
   * Function to navigate to initial pop over state
   * @example navigateToInitialPopOverState()
   */
  const navigateToInitialPopOverState = () => {
    initialPopOver();
    setShowDetailedPopOverMenu(false);
    setPopOverMenuItems(initialPopOverMenuList);
  };
  /**
   * Function to display inital popover when popover icon is clicked
   * @example initialPopOver()
   */
  const initialPopOver = () => {
    return (
      <CcfTypography>
        <CcfPopOver
          onPopOverItemSelection={onPopOverItemSelection}
          navigateToInitialPopOverState={navigateToInitialPopOverState}
          optionList={popOverMenuItems}
        ></CcfPopOver>
      </CcfTypography>
    );
  };
  return (
    <>
      <div className={styles.controlHeader}>
        <CcfHeader
          LeftIcon={
            props.contact.media === MediaType.VOICE ||
            props.contact.media === MediaType.VOICEMAIL
              ? IconsLeftMap.get(props.contact.media)
              : props.contact.channel
              ? IconsLeftMap.get(props.contact.channel)
              : undefined
          }
          headerText={props.headerText}
          headerTextClassess={props.headerTextClassess}
          RightIcon={false}
        />
        {initialPopOver()}
      </div>
      <Divider variant="fullWidth" className="horizontalDivider" />
    </>
  );
}

export default ContactControlPanelHeading;
